export type TeamMember = {
  id: string;
  name: string;
  role: string;
  color: string;
};

export type TeamScheduleItem = {
  id: number;
  subject: string;
  startTime: Date;
  endTime: Date;
  isAllDay: boolean;
  ownerId: string;
  ownerName: string;
  description: string;
};

export type ProjectItem = {
  id: number;
  subject: string;
  assignee: string;
  resources: string;
  location: string;
  startTime: Date;
  endTime: Date;
  progress: number;
  description: string;
  predecessor: string;
};

export type ProjectDefinition = {
  id: string;
  name: string;
  memberIds: string[];
  teamScheduleItems: TeamScheduleItem[];
  projectItems: ProjectItem[];
};

export const teamMembers: TeamMember[] = [
  { id: "ava", name: "Ava Martinez", role: "Project Manager", color: "#2563EB" },
  { id: "liam", name: "Liam Chen", role: "Creative Lead", color: "#7C3AED" },
  { id: "maya", name: "Maya Patel", role: "Operations", color: "#EA580C" },
  { id: "noah", name: "Noah Johnson", role: "Production", color: "#059669" },
  { id: "sophia", name: "Sophia Kim", role: "Brand Strategist", color: "#DB2777" },
  { id: "ethan", name: "Ethan Brooks", role: "Logistics", color: "#0891B2" },
];

export const projectDefinitions: ProjectDefinition[] = [
  {
    id: "chivino-launch",
    name: "Chivino Launch",
    memberIds: ["ava", "liam", "maya", "noah"],
    teamScheduleItems: [
      {
        id: 101,
        subject: "Weekly planning",
        startTime: new Date("2026-03-23T09:00:00"),
        endTime: new Date("2026-03-23T10:00:00"),
        isAllDay: false,
        ownerId: "ava",
        ownerName: "Ava Martinez",
        description: "Kickoff meeting for the week.",
      },
      {
        id: 102,
        subject: "Design review",
        startTime: new Date("2026-03-23T11:00:00"),
        endTime: new Date("2026-03-23T12:00:00"),
        isAllDay: false,
        ownerId: "liam",
        ownerName: "Liam Chen",
        description: "Review concept updates and final assets.",
      },
      {
        id: 103,
        subject: "Venue coordination",
        startTime: new Date("2026-03-24T14:00:00"),
        endTime: new Date("2026-03-24T15:30:00"),
        isAllDay: false,
        ownerId: "maya",
        ownerName: "Maya Patel",
        description: "Confirm venue logistics and staffing.",
      },
      {
        id: 104,
        subject: "Production sync",
        startTime: new Date("2026-03-25T10:00:00"),
        endTime: new Date("2026-03-25T11:30:00"),
        isAllDay: false,
        ownerId: "noah",
        ownerName: "Noah Johnson",
        description: "Walk through handoff and readiness.",
      },
      {
        id: 105,
        subject: "Client status update",
        startTime: new Date("2026-03-26T13:00:00"),
        endTime: new Date("2026-03-26T14:00:00"),
        isAllDay: false,
        ownerId: "ava",
        ownerName: "Ava Martinez",
        description: "Share weekly progress and timeline changes.",
      },
      {
        id: 106,
        subject: "Asset QA",
        startTime: new Date("2026-03-27T09:30:00"),
        endTime: new Date("2026-03-27T11:00:00"),
        isAllDay: false,
        ownerId: "liam",
        ownerName: "Liam Chen",
        description: "Final pass on all release assets.",
      },
    ],
    projectItems: [
      {
        id: 1,
        subject: "Campaign planning",
        assignee: "ava",
        resources: "Timeline brief",
        location: "HQ Board Room",
        startTime: new Date("2026-03-23T09:00:00"),
        endTime: new Date("2026-03-23T11:00:00"),
        progress: 100,
        description: "Confirm goals, scope, and owners.",
        predecessor: "",
      },
      {
        id: 2,
        subject: "Creative review",
        assignee: "liam",
        resources: "Concept deck",
        location: "Creative Studio",
        startTime: new Date("2026-03-24T13:00:00"),
        endTime: new Date("2026-03-24T15:00:00"),
        progress: 65,
        description: "Review concepts and gather revisions.",
        predecessor: "1FS",
      },
      {
        id: 3,
        subject: "Production handoff",
        assignee: "noah",
        resources: "Delivery checklist",
        location: "Production Floor",
        startTime: new Date("2026-03-25T10:00:00"),
        endTime: new Date("2026-03-25T12:30:00"),
        progress: 35,
        description: "Align delivery requirements with production.",
        predecessor: "2FS",
      },
      {
        id: 4,
        subject: "Launch day",
        assignee: "maya",
        resources: "Run of show",
        location: "Event Venue",
        startTime: new Date("2026-03-27T00:00:00"),
        endTime: new Date("2026-03-27T23:59:00"),
        progress: 0,
        description: "Monitor the rollout and respond to issues.",
        predecessor: "3FS",
      },
    ],
  },
  {
    id: "spring-showcase",
    name: "Spring Showcase",
    memberIds: ["ava", "sophia", "maya", "ethan"],
    teamScheduleItems: [
      {
        id: 201,
        subject: "Sponsor alignment",
        startTime: new Date("2026-03-23T10:00:00"),
        endTime: new Date("2026-03-23T11:30:00"),
        isAllDay: false,
        ownerId: "ava",
        ownerName: "Ava Martinez",
        description: "Finalize sponsor commitments and deliverables.",
      },
      {
        id: 202,
        subject: "Messaging workshop",
        startTime: new Date("2026-03-24T09:30:00"),
        endTime: new Date("2026-03-24T11:00:00"),
        isAllDay: false,
        ownerId: "sophia",
        ownerName: "Sophia Kim",
        description: "Refine showcase messaging and CTA direction.",
      },
      {
        id: 203,
        subject: "Transport audit",
        startTime: new Date("2026-03-25T13:00:00"),
        endTime: new Date("2026-03-25T14:00:00"),
        isAllDay: false,
        ownerId: "ethan",
        ownerName: "Ethan Brooks",
        description: "Confirm load-in timing and equipment routing.",
      },
      {
        id: 204,
        subject: "Staffing check-in",
        startTime: new Date("2026-03-26T15:00:00"),
        endTime: new Date("2026-03-26T16:00:00"),
        isAllDay: false,
        ownerId: "maya",
        ownerName: "Maya Patel",
        description: "Lock assignments for onsite operations.",
      },
    ],
    projectItems: [
      {
        id: 1,
        subject: "Venue confirmation",
        assignee: "maya",
        resources: "Venue packet",
        location: "Downtown Hall",
        startTime: new Date("2026-03-23T08:00:00"),
        endTime: new Date("2026-03-23T12:00:00"),
        progress: 85,
        description: "Close venue paperwork and floor plan details.",
        predecessor: "",
      },
      {
        id: 2,
        subject: "Sponsor rollout",
        assignee: "ava",
        resources: "Sponsor deck",
        location: "Client Zoom",
        startTime: new Date("2026-03-24T09:00:00"),
        endTime: new Date("2026-03-24T12:00:00"),
        progress: 45,
        description: "Launch sponsor communications and approvals.",
        predecessor: "1FS",
      },
      {
        id: 3,
        subject: "Campaign copy",
        assignee: "sophia",
        resources: "Copy draft",
        location: "Marketing Room",
        startTime: new Date("2026-03-25T10:00:00"),
        endTime: new Date("2026-03-25T15:00:00"),
        progress: 20,
        description: "Write final launch copy and asset notes.",
        predecessor: "2FS",
      },
      {
        id: 4,
        subject: "Load-in run",
        assignee: "ethan",
        resources: "Truck manifest",
        location: "Venue Dock",
        startTime: new Date("2026-03-26T09:00:00"),
        endTime: new Date("2026-03-26T13:00:00"),
        progress: 10,
        description: "Validate equipment movement and timing.",
        predecessor: "3FS",
      },
    ],
  },
  {
    id: "artisan-retreat",
    name: "Artisan Retreat",
    memberIds: ["liam", "sophia", "noah", "ethan"],
    teamScheduleItems: [
      {
        id: 301,
        subject: "Moodboard prep",
        startTime: new Date("2026-03-23T08:30:00"),
        endTime: new Date("2026-03-23T09:30:00"),
        isAllDay: false,
        ownerId: "liam",
        ownerName: "Liam Chen",
        description: "Build references for the retreat visual system.",
      },
      {
        id: 302,
        subject: "Program narrative",
        startTime: new Date("2026-03-24T14:00:00"),
        endTime: new Date("2026-03-24T15:30:00"),
        isAllDay: false,
        ownerId: "sophia",
        ownerName: "Sophia Kim",
        description: "Draft narrative flow for all retreat sessions.",
      },
      {
        id: 303,
        subject: "Fabrication review",
        startTime: new Date("2026-03-25T09:00:00"),
        endTime: new Date("2026-03-25T10:30:00"),
        isAllDay: false,
        ownerId: "noah",
        ownerName: "Noah Johnson",
        description: "Check readiness of signage and staging pieces.",
      },
      {
        id: 304,
        subject: "Transport rehearsal",
        startTime: new Date("2026-03-26T11:00:00"),
        endTime: new Date("2026-03-26T12:00:00"),
        isAllDay: false,
        ownerId: "ethan",
        ownerName: "Ethan Brooks",
        description: "Sequence deliveries for retreat setup day.",
      },
    ],
    projectItems: [
      {
        id: 1,
        subject: "Theme design",
        assignee: "liam",
        resources: "Visual references",
        location: "Studio 2",
        startTime: new Date("2026-03-23T08:00:00"),
        endTime: new Date("2026-03-23T12:00:00"),
        progress: 55,
        description: "Create visual direction for the retreat.",
        predecessor: "",
      },
      {
        id: 2,
        subject: "Session outline",
        assignee: "sophia",
        resources: "Program brief",
        location: "Strategy Room",
        startTime: new Date("2026-03-24T10:00:00"),
        endTime: new Date("2026-03-24T13:00:00"),
        progress: 30,
        description: "Detail the agenda and facilitator notes.",
        predecessor: "1FS",
      },
      {
        id: 3,
        subject: "Build review",
        assignee: "noah",
        resources: "Material list",
        location: "Workshop",
        startTime: new Date("2026-03-25T12:00:00"),
        endTime: new Date("2026-03-25T16:00:00"),
        progress: 15,
        description: "Review installation and staging build status.",
        predecessor: "2FS",
      },
      {
        id: 4,
        subject: "Arrival setup",
        assignee: "ethan",
        resources: "Transport plan",
        location: "Retreat Site",
        startTime: new Date("2026-03-26T09:00:00"),
        endTime: new Date("2026-03-26T17:00:00"),
        progress: 0,
        description: "Prepare check-in, delivery, and staff flow.",
        predecessor: "3FS",
      },
    ],
  },
];

export const defaultProjectId = projectDefinitions[0].id;

export const cloneProjectItem = (item: ProjectItem): ProjectItem => ({
  ...item,
  startTime: new Date(item.startTime),
  endTime: new Date(item.endTime),
});

export const cloneTeamScheduleItem = (item: TeamScheduleItem): TeamScheduleItem => ({
  ...item,
  startTime: new Date(item.startTime),
  endTime: new Date(item.endTime),
});
